import React, { useEffect, useState } from 'react';
import AddButton from 'components/v2/buttons/AddButton';
import Card from 'components/v2/card/Card';
import LayoutWithSidebar from 'components/v2/LayoutWithSidebar';
import { useTranslation } from 'react-i18next';
import SearchField from 'components/v2/SearchField';
import { useWarehousesWithAssociation } from 'hooks/warehouse/useManageWarehouse';
import LoadingIndicator from 'components/v2/LoadingIndicator';
import SessionStore from 'stores/SessionStore';
import AddWarehouseForm from './AddWarehouse';
import WarehouseListItem from './WarehouseListItem';

const ManageWarehouse = () => {
  const { t } = useTranslation();
  const [showAddWarehouseForm, setShowAddWarehouseForm] = useState(false);
  const [query, setQuery] = useState('');
  const [warehouseData, setWarehouseData] = useState([]);
  const [queryData, setQueryData] = useState([]);
  const { data = [], isLoading } = useWarehousesWithAssociation();

  const canPerformActions = SessionStore.hasShipperOnboardingRole();

  const populateData = (inputWarehouseData = {}) => {
    if (!Object.keys(inputWarehouseData).length) {
      return;
    }

    const {
      location,
      shippers: shippersData,
      _id,
      active,
    } = inputWarehouseData;
    const address = location?.address;
    const warehouse_location = location?._id;
    const shippers = shippersData.map(({ shipper, relations }) => {
      return {
        ...shipper,
        isFromApi: true,
        warehouseUsers: relations.map(
          ({ warehouse_user, active, status, _id: relation_id }) => ({
            ...warehouse_user,
            relation_id,
            isFromApi: true,
            active,
            status,
          }),
        ),
      };
    });
    return {
      address,
      warehouse_location,
      shippers,
      _id,
      active,
      isFromApi: true,
    };
  };

  useEffect(() => {
    if (data?.length) {
      setWarehouseData(data.map((d) => populateData(d)));
    }
  }, [data]);

  useEffect(() => {
    if (query.length >= 2) {
      const searchData = warehouseData.filter((warehouse) =>
        warehouse.address.toLowerCase().includes(query.toLowerCase().trim()),
      );
      setQueryData(searchData);
    } else {
      setQueryData([]);
    }
  }, [query]);

  const breadcrumbs = {
    pages: [
      {
        name: t('WAREHOUSE'),
        href: `/manage/warehouse`,
        current: true,
      },
    ],
  };

  function renderHeading() {
    return (
      <div
        data-testid="manage-warehouse-title"
        className="mb-4 flex items-center space-x-4"
      >
        <h3 className="">{t('WAREHOUSE', { count: 2 })}</h3>
        {canPerformActions && (
          <AddButton
            size="medium"
            onClick={() => setShowAddWarehouseForm(true)}
          />
        )}
      </div>
    );
  }

  const renderAddWarehouseForm = () => {
    if (!showAddWarehouseForm) {
      return null;
    }
    return (
      <AddWarehouseForm
        data={{}}
        onCancel={() => setShowAddWarehouseForm(false)}
      />
    );
  };

  const showEmptyWarehouseMessage = () => (
    <p data-testid="manage-warehouse-empty-message">
      {t('EMPTY_WAREHOUSE_MSG')}
    </p>
  );

  const renderWarehouseList = (data = []) => {
    if (!data.length) {
      return showEmptyWarehouseMessage();
    }
    return data.map((warehouse, i) => {
      return (
        <>
          <WarehouseListItem
            data={warehouse}
            key={i}
            actionsEnabled={canPerformActions}
          />
          {i !== data.length - 1 && <hr className="m-0" />}
        </>
      );
    });
  };

  const renderWarehouses = () => {
    if (isLoading) {
      return <LoadingIndicator />;
    }
    if (!warehouseData?.length) {
      return showEmptyWarehouseMessage();
    }

    const showSearchData = query && query.length >= 2;
    return (
      <Card className="mb-14">
        {showSearchData &&
          (queryData.length > 0
            ? renderWarehouseList(queryData)
            : showEmptyWarehouseMessage())}
        {!showSearchData && renderWarehouseList(warehouseData)}
      </Card>
    );
  };

  return (
    <LayoutWithSidebar className="mt-4 p-0" breadcrumbs={breadcrumbs}>
      <div data-testid="manage-warehouse-page" className="lx:w-1/2 w-full">
        {renderHeading()}

        <div
          data-testid="manage-warehouse-address-search"
          className="mt-4 mb-2"
        >
          <SearchField
            placeholder={t('SEARCH_LOCATION_EXISTING')}
            value={query}
            onChange={(val) => setQuery(val)}
          />
        </div>
        {renderAddWarehouseForm()}
        {renderWarehouses()}
      </div>
    </LayoutWithSidebar>
  );
};

export default ManageWarehouse;
